import Spinner from "./spinner";

// The visitor's past conversations, newest first. A conversation's name is the
// title the bot owner gave it, else the visitor's first question.
const ChatHistoryPanel = ({
  isLoadingHistory,
  isLoadingChat,
  fetchedChatHistory,
  currentSessionId,
  accentStyle,
  primaryColor,
  onClose,
  onLoad,
  onDelete,
}) => {
  const chats = [...fetchedChatHistory].sort(
    (a, b) => (b.updated_at || 0) - (a.updated_at || 0),
  );

  return (
    <div className="flex flex-col overflow-y-auto p-4 w-[95%] h-full mb-4 space-y-2 bg-white rounded-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Chat History</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          Close
        </button>
      </div>

      {isLoadingHistory ? (
        <div className="text-center py-12 text-gray-500">
          <div className="flex justify-center mb-4">
            <Spinner size="h-16 w-16" />
          </div>
          <p className="text-sm">Loading chat history...</p>
        </div>
      ) : chats.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <svg
            className="w-16 h-16 mx-auto mb-4 opacity-50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
          <p className="text-sm">No chat history yet</p>
          <p className="text-xs mt-1">Start a conversation to see it here</p>
        </div>
      ) : (
        chats.map((chat) => {
          const name = chat.title || chat.user_preview || "Untitled chat";
          const when = new Date(chat.updated_at || chat.created_at || Date.now());
          const isCurrent = chat.session_id === currentSessionId;
          // The open conversation is already on screen; picking it just goes back.
          const open = () => {
            if (isLoadingChat) return;
            if (isCurrent) onClose();
            else onLoad(chat.session_id);
          };
          return (
            <div
              key={chat.session_id}
              className={`flex flex-row gap-0 rounded-[10px] hover:shadow-md transition-shadow group ${isLoadingChat ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {/* Date badge */}
              <div
                className="text-center flex items-center p-2 px-3 text-sm font-semibold rounded-l-[10px] bg-[#F4F4F4] whitespace-nowrap"
                style={accentStyle}
                onClick={open}
              >
                {when.toLocaleDateString([], { month: "short", day: "numeric" })}
              </div>
              <div className="w-[1px] bg-white" />

              {/* Chat preview */}
              <div
                className="flex-1 min-w-0 p-3 flex items-center justify-between bg-[#F4F4F4]"
                onClick={open}
              >
                <div className="flex flex-col gap-1 min-w-0">
                  <p className="text-xs text-gray-500/70">
                    {when.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    <span className="mx-1">•</span>
                    {chat.message_count || 0} messages
                    {isCurrent && (
                      <span
                        className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Current
                      </span>
                    )}
                  </p>
                  <p className="text-sm font-medium text-gray-800 line-clamp-2 break-words">
                    {name}
                  </p>
                </div>

                {/* Remove button — visible on hover. Only this visitor's list
                    changes; the bot owner keeps the conversation. */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isLoadingChat) onDelete(chat.session_id);
                  }}
                  disabled={isLoadingChat}
                  className="opacity-0 group-hover:opacity-100 ml-2 text-red-500 hover:text-red-700 transition-opacity disabled:cursor-not-allowed shrink-0"
                  title="Remove from history"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              {/* Select arrow */}
              <div
                className="rounded-r-[10px] flex justify-center items-center px-3"
                style={{ backgroundColor: primaryColor }}
                onClick={open}
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ChatHistoryPanel;
